import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { FullPageLoader } from '@/components/feedback/States'
import { platformApi, platformHttp, setPlatformRefreshHandler, setPlatformToken } from './http'
import { platformKeys } from './api'
import type { Operator, OperatorSession } from './types'
import { PlatformAuthContext, usePlatformAuth, type PlatformAuthValue } from './usePlatformAuth'

/** The operator session: restored from its refresh cookie on boot and refreshed before expiry. */
export function PlatformAuthProvider({ children }: { children?: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<PlatformAuthValue['status']>('loading')
  const [operator, setOperator] = useState<Operator | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const applyRef = useRef<(s: OperatorSession) => void>(() => undefined)

  const clear = useCallback(() => {
    window.clearTimeout(timer.current)
    setPlatformToken(null)
    setOperator(null)
    setStatus('anonymous')
    queryClient.removeQueries({ queryKey: platformKeys.all })
  }, [queryClient])

  const refreshSession = () => platformApi.post<OperatorSession>('/auth/refresh')

  const apply = useCallback(
    (session: OperatorSession) => {
      setPlatformToken(session.accessToken)
      setOperator(session.operator)
      setStatus('authenticated')
      window.clearTimeout(timer.current)
      timer.current = window.setTimeout(
        () => void refreshSession().then((s) => applyRef.current(s), clear),
        Math.max(10, session.expiresIn - 60) * 1000,
      )
    },
    [clear],
  )

  useEffect(() => {
    applyRef.current = apply
  }, [apply])

  useEffect(() => {
    setPlatformRefreshHandler(async () => {
      try {
        const s = await refreshSession()
        applyRef.current(s)
        return s.accessToken
      } catch {
        clear()
        return null
      }
    })
    refreshSession().then(apply, clear)
    return () => {
      setPlatformRefreshHandler(null)
      window.clearTimeout(timer.current)
    }
  }, [apply, clear])

  const login = useCallback(
    async (email: string, password: string) => {
      apply(await platformApi.post<OperatorSession>('/auth/login', { email, password }))
    },
    [apply],
  )

  const logout = useCallback(async () => {
    try {
      await platformHttp.post('/auth/logout')
    } finally {
      clear()
    }
  }, [clear])

  const value = useMemo(() => ({ status, operator, login, logout }), [status, operator, login, logout])
  return <PlatformAuthContext value={value}>{children ?? <Outlet />}</PlatformAuthContext>
}

/** Sends visitors without an operator session to the platform sign-in page. */
export function RequireOperator() {
  const { status } = usePlatformAuth()
  const location = useLocation()
  if (status === 'loading') return <FullPageLoader />
  if (status === 'anonymous') {
    return (
      <Navigate
        to={`/platform/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    )
  }
  return <Outlet />
}
